import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, VStack, Stack, Select, Input,
  IconButton, Tooltip, useToast, SimpleGrid, Divider, Alert, AlertIcon,
  InputGroup, InputLeftElement, Table, Thead, Tbody, Tr, Th, Td,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  Tabs, TabList, Tab, TabPanels, TabPanel,
} from "@chakra-ui/react";
import {
  RefreshCw, Search, FileText, Download, AlertTriangle, CheckCircle,
  Clock, HelpCircle, XCircle, ReceiptText, Ban,
} from "lucide-react";
import {
  getNotasDevolucaoVenda, getNotaDevolucaoVenda, baixarArquivoNotaDevolucaoVenda,
  reprocessarNotaDevolucaoVenda,
} from "../services/api";
import { formatBRL, formatDate, formatDateTime } from "../utils/formatters";
import PainelNotaDevolucao, { seloAmbienteNota } from "../components/PainelNotaDevolucao";
import ConfigNotaDevolucao from "../components/ConfigNotaDevolucao";
import FilaVendasCanceladas from "../components/FilaVendasCanceladas";
import RelatorioDifalDevolucoes from "../components/RelatorioDifalDevolucoes";

const ACCENT = "#F68633";

export const STATUS_NOTA_DEVOLUCAO = {
  pendente: { label: "Na fila", curto: "Na fila", cor: "gray", icone: Clock },
  aguardando_confirmacao: { label: "Aguardando confirmação", curto: "Confirmar", cor: "orange", icone: HelpCircle },
  emitida: { label: "Emitida", curto: "Emitida", cor: "green", icone: CheckCircle },
  dispensada_nota_cliente: { label: "Nota do cliente", curto: "Nota cliente", cor: "blue", icone: ReceiptText },
  erro: { label: "Erro", curto: "Erro", cor: "red", icone: AlertTriangle },
  cancelada: { label: "Cancelada", curto: "Cancelada", cor: "gray", icone: XCircle },
  bloqueada: { label: "Bloqueada", curto: "Bloqueada", cor: "yellow", icone: Ban },
};

// Mercado Livre vira "ML" e ECOMMERCE vira "Site": e o vocabulario que o
// pos-venda usa, e o rotulo inteiro nao cabe ao lado do numero do pedido.
const canalCurto = (origem) => {
  if (!origem) return null;
  return String(origem).toUpperCase().includes("ECOMMERCE") ? "Site" : "ML";
};

// Bloco de identificacao da linha, no mesmo desenho de /melhor-envio: o numero
// do pedido e o que o pos-venda tem em maos, o id interno so serve de apoio.
function IdsDevolucao({ r, compacto = false }) {
  const canal = canalCurto(r.venda_origem);
  const tamanhoRotulo = compacto ? "0.55rem" : "0.6rem";

  return (
    <Box>
      <HStack spacing={2}>
        <Text fontSize={compacto ? "sm" : "xs"} fontWeight="bold" color="gray.700" lineHeight="1.3">
          {r.devolucao_id ? `Devolução #${r.devolucao_id}` : `Venda #${r.venda_id}`}
        </Text>
        {r.tipo === "retorno_nao_entregue" && (
          <Badge fontSize="9px" colorScheme="purple" variant="subtle">Recusa</Badge>
        )}
      </HStack>
      <Flex gap={3} mt={1} wrap="wrap">
        {r.venda_id && (
          <Box>
            <Text fontSize={tamanhoRotulo} color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight="1">
              ID TAGA
            </Text>
            <Text fontSize="xs" color="gray.700" fontWeight="semibold">{r.venda_id}</Text>
          </Box>
        )}
        {r.venda_id_origem && (
          <Box minW={0}>
            <Text fontSize={tamanhoRotulo} color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight="1">
              ID Origem{canal ? ` (${canal})` : ""}
            </Text>
            <Text fontSize="xs" color="blue.600" fontWeight="bold" wordBreak="break-all">
              {r.venda_id_origem}
            </Text>
          </Box>
        )}
      </Flex>
    </Box>
  );
}

// `curto` no cartao do celular: o rotulo inteiro empurra o titulo para duas linhas.
export const seloNotaDevolucao = (status, curto = false) => {
  const s = STATUS_NOTA_DEVOLUCAO[status] || { label: status, cor: "gray" };
  return (
    <Badge colorScheme={s.cor} fontSize="11px" px={2} py={0.5} borderRadius="md" whiteSpace="nowrap">
      {curto ? (s.curto || s.label) : s.label}
    </Badge>
  );
};

// Cards do topo. Só os quatro que mudam decisão: o que precisa de gente, o que
// falhou, o que saiu e o que o cliente cobriu.
const CARDS = [
  { chave: "aguardando_confirmacao", titulo: "Aguardando confirmação", cor: "orange.500" },
  { chave: "erro", titulo: "Com erro", cor: "red.500" },
  { chave: "bloqueada", titulo: "Bloqueadas", cor: "yellow.600" },
  { chave: "emitida", titulo: "Emitidas", cor: "green.500" },
  { chave: "dispensada_nota_cliente", titulo: "Nota do cliente", cor: "blue.500" },
];

export default function NfeDevolucaoCliente() {
  const toast = useToast();

  const [lista, setLista] = useState([]);
  const [resumo, setResumo] = useState({});
  const [config, setConfig] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [filtros, setFiltros] = useState({ status: "", cliente_tipo: "", busca: "" });
  const [detalheId, setDetalheId] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const res = await getNotasDevolucaoVenda({
      status: filtros.status || undefined,
      cliente_tipo: filtros.cliente_tipo || undefined,
      busca: filtros.busca || undefined,
    });
    setLista(res?.data || []);
    setResumo(res?.resumo || {});
    if (res?.config) setConfig(res.config);
    setCarregando(false);
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    // A sidebar é fixa (64px recolhida) e a navbar do topo tem 56px: sem esse
    // deslocamento o conteúdo encosta no menu da esquerda.
    <Box ml={{ base: 0, md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
      <Flex justify="space-between" align="center" mb={1} wrap="wrap" gap={3}>
        <HStack spacing={3}>
          <ReceiptText size={26} color={ACCENT} />
          <Box>
            <Text fontSize="xl" fontWeight="bold">NF-e de Devolução do Cliente</Text>
            <Text fontSize="xs" color="gray.500">
              Nota de entrada emitida quando a peça do cliente volta e é marcada como recebida
            </Text>
          </Box>
        </HStack>
        <Tooltip label="Atualizar">
          <IconButton size="sm" variant="outline" aria-label="Atualizar"
            icon={<RefreshCw size={16} />} onClick={carregar} isLoading={carregando} />
        </Tooltip>
      </Flex>

      {config && (
        <Box mt={4}>
          <ConfigNotaDevolucao config={config} onSalvo={setConfig} toast={toast} />
        </Box>
      )}

      <Tabs colorScheme="orange" isLazy mt={config ? 0 : 4}>
        <TabList overflowX="auto" overflowY="hidden" mb={4} sx={{ scrollbarWidth: "none" }}>
          <Tab fontSize="sm" whiteSpace="nowrap">Notas</Tab>
          <Tab fontSize="sm" whiteSpace="nowrap">Pedidos cancelados</Tab>
          <Tab fontSize="sm" whiteSpace="nowrap">DIFAL</Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>

      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
        {CARDS.map((card) => (
          <Box key={card.chave} bg="white" borderWidth="1px" borderRadius="lg" p={3}
            cursor="pointer" transition="all .15s"
            _hover={{ shadow: "sm", borderColor: ACCENT }}
            onClick={() => setFiltros((f) => ({ ...f, status: f.status === card.chave ? "" : card.chave }))}
            borderColor={filtros.status === card.chave ? ACCENT : "gray.200"}>
            <Text fontSize="11px" color="gray.500" noOfLines={1}>{card.titulo}</Text>
            <Text fontSize="2xl" fontWeight="bold" color={card.cor} lineHeight="1.2">
              {resumo[card.chave] ?? 0}
            </Text>
          </Box>
        ))}
      </SimpleGrid>

      {(resumo.aguardando_confirmacao ?? 0) > 0 && (
        <Alert status="warning" borderRadius="lg" mb={4} fontSize="sm">
          <AlertIcon />
          <Text>
            {resumo.aguardando_confirmacao} devolução(ões) de cliente CNPJ esperando alguém dizer se a
            nota que o cliente emitiu cobre o recebimento. Enquanto isso, nenhuma nota é emitida.
          </Text>
        </Alert>
      )}

      <Flex gap={3} mb={4} wrap="wrap">
        <InputGroup size="sm" maxW="340px">
          <InputLeftElement pointerEvents="none"><Search size={14} /></InputLeftElement>
          <Input placeholder="Cliente, pedido, devolução, venda, NF-e ou chave"
            bg="white"
            value={filtros.busca}
            onChange={(e) => setFiltros((f) => ({ ...f, busca: e.target.value }))} />
        </InputGroup>
        <Select size="sm" maxW="220px" bg="white" value={filtros.status}
          onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}>
          <option value="">Todos os status</option>
          {Object.entries(STATUS_NOTA_DEVOLUCAO).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
        <Select size="sm" maxW="160px" bg="white" value={filtros.cliente_tipo}
          onChange={(e) => setFiltros((f) => ({ ...f, cliente_tipo: e.target.value }))}>
          <option value="">CPF e CNPJ</option>
          <option value="CPF">Só CPF</option>
          <option value="CNPJ">Só CNPJ</option>
        </Select>
      </Flex>

      {carregando ? (
        <Center py={16}><Spinner color={ACCENT} /></Center>
      ) : lista.length === 0 ? (
        <Center py={16}>
          <VStack spacing={2}>
            <FileText size={36} color="#CBD5E0" />
            <Text color="gray.500" fontSize="sm">Nenhuma nota emitida por aqui ainda.</Text>
          </VStack>
        </Center>
      ) : (
        <>
        {/* Mobile: cartões. Numa tabela de 8 colunas o status, que é a
            informação principal, só aparece depois de rolar de lado. */}
        <Stack display={{ base: "flex", md: "none" }} spacing={2}>
          {lista.map((r) => (
            <Box key={r.id} bg="white" borderWidth="1px" borderRadius="lg" p={3}
              onClick={() => setDetalheId(r.id)} cursor="pointer" _active={{ bg: "gray.50" }}>
              <Flex justify="space-between" align="flex-start" gap={2}>
                <Box minW={0}>
                  <IdsDevolucao r={r} compacto />
                  <Text fontSize="xs" color="gray.600" noOfLines={1} mt={1}>{r.cliente_nome || "—"}</Text>
                </Box>
                <Stack spacing={1} flexShrink={0} align="flex-end">
                  {seloNotaDevolucao(r.status, true)}
                  {r.status === "emitida" && seloAmbienteNota(r.ambiente, true)}
                </Stack>
              </Flex>
              <HStack spacing={2} mt={2} wrap="wrap" fontSize="11px" color="gray.500">
                {r.cliente_tipo && (
                  <Badge fontSize="9px" colorScheme={r.cliente_tipo === "CNPJ" ? "purple" : "gray"}>
                    {r.cliente_tipo}
                  </Badge>
                )}
                {r.origem_nfe_numero && <Text>NF venda {r.origem_nfe_numero}</Text>}
                <Text>{formatDate(r.created_at)}</Text>
              </HStack>
              <Flex justify="space-between" align="center" mt={2}>
                <Text fontSize="xs" color="gray.600">
                  {r.nfe_numero
                    ? `NF-e ${r.nfe_numero}`
                    : r.nota_cliente_numero
                      ? `Nota do cliente ${r.nota_cliente_numero}`
                      : `${r.itens_count ?? 0} item(ns)`}
                </Text>
                <Text fontSize="sm" fontWeight="semibold">{formatBRL(r.totais?.vNF || 0)}</Text>
              </Flex>
            </Box>
          ))}
        </Stack>

        <Box display={{ base: "none", md: "block" }} overflowX="auto" borderWidth="1px" borderRadius="lg" bg="white">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Devolução</Th>
                <Th>Cliente</Th>
                <Th>NF de venda</Th>
                <Th isNumeric>Itens</Th>
                <Th isNumeric>Valor</Th>
                <Th>Nota</Th>
                <Th>Status</Th>
                <Th>Recebida</Th>
              </Tr>
            </Thead>
            <Tbody>
              {lista.map((r) => (
                <Tr key={r.id} _hover={{ bg: "gray.50", cursor: "pointer" }}
                  onClick={() => setDetalheId(r.id)}>
                  <Td minW="210px"><IdsDevolucao r={r} /></Td>
                  <Td fontSize="xs">
                    <Text noOfLines={1} maxW="220px">{r.cliente_nome || "—"}</Text>
                    {r.cliente_tipo && (
                      <Badge fontSize="9px" colorScheme={r.cliente_tipo === "CNPJ" ? "purple" : "gray"}>
                        {r.cliente_tipo}
                      </Badge>
                    )}
                  </Td>
                  <Td fontSize="xs">{r.origem_nfe_numero || "—"}</Td>
                  <Td isNumeric fontSize="xs">{r.itens_count ?? 0}</Td>
                  <Td isNumeric fontSize="xs">{formatBRL(r.totais?.vNF || 0)}</Td>
                  <Td fontSize="xs">
                    {r.nfe_numero
                      ? <Text>NF-e {r.nfe_numero}</Text>
                      : r.nota_cliente_numero
                        ? <Text color="blue.600">Cliente {r.nota_cliente_numero}</Text>
                        : "—"}
                  </Td>
                  <Td>
                    <HStack spacing={1} wrap="wrap">
                      {seloNotaDevolucao(r.status)}
                      {r.status === "emitida" && seloAmbienteNota(r.ambiente, true)}
                    </HStack>
                  </Td>
                  <Td fontSize="xs" color="gray.500">{formatDate(r.created_at)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        </>
      )}

          </TabPanel>
          <TabPanel p={0}>
            <FilaVendasCanceladas toast={toast} />
          </TabPanel>
          <TabPanel p={0}>
            <RelatorioDifalDevolucoes toast={toast} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {detalheId && (
        <DrawerDetalhe
          id={detalheId}
          onFechar={() => { setDetalheId(null); carregar(); }}
          toast={toast}
        />
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------------- */
/* Detalhe: o que voltou, de qual venda, e o estado da nota                    */
/* ------------------------------------------------------------------------- */

function DrawerDetalhe({ id, onFechar, toast }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [baixando, setBaixando] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    const res = await getNotaDevolucaoVenda(id);
    setDados(res?.error ? null : res);
    setCarregando(false);
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  const baixar = async (tipo) => {
    setBaixando(tipo);
    const res = await baixarArquivoNotaDevolucaoVenda(id, tipo);
    setBaixando("");
    if (res?.error) {
      toast({ title: "Não foi possível baixar", description: res.error, status: "error", duration: 5000 });
    }
  };

  const registro = dados?.registro;

  return (
    <Drawer isOpen placement="right" size="lg" onClose={onFechar}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <HStack spacing={3}>
            <Text fontSize="md">
              {registro?.devolucao_id ? `Devolução #${registro.devolucao_id}` : registro?.venda_id ? `Venda #${registro.venda_id}` : `Registro #${id}`}
            </Text>
            {registro && seloNotaDevolucao(registro.status)}
            {registro?.status === "emitida" && seloAmbienteNota(registro.ambiente, true)}
          </HStack>
        </DrawerHeader>

        <DrawerBody bg="gray.50" px={{ base: 3, md: 5 }} py={4}>
          {carregando ? (
            <Center py={16}><Spinner color={ACCENT} /></Center>
          ) : !registro ? (
            <Alert status="error" borderRadius="md"><AlertIcon />Registro não encontrado.</Alert>
          ) : (
            <VStack align="stretch" spacing={4}>
              <PainelNotaDevolucao
                dados={dados}
                onAtualizado={(novo) => setDados(novo)}
                onReprocessar={async () => {
                  const res = await reprocessarNotaDevolucaoVenda(id);
                  if (res?.error) {
                    toast({ title: "Erro", description: res.error, status: "error", duration: 5000 });
                  } else {
                    toast({ title: "Reenfileirada", status: "success", duration: 3000 });
                    setDados(res);
                  }
                }}
                toast={toast}
              />

              <Box bg="white" borderWidth="1px" borderRadius="lg" p={4}>
                <Text fontSize="sm" fontWeight="semibold" mb={3}>Origem</Text>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} fontSize="xs">
                  <Info rotulo="Venda" valor={dados?.venda?.id ? `#${dados.venda.id}` : "—"} />
                  <Info rotulo="Marketplace" valor={dados?.venda?.origem || "—"} />
                  <Info rotulo="Pedido" valor={dados?.venda?.id_origem || "—"} />
                  <Info rotulo="NF-e de venda" valor={registro.origem_nfe_numero || "—"} />
                  <Info rotulo="Emissão" valor={formatDate(registro.origem_nfe_emissao)} />
                  <Info rotulo="Chave" valor={registro.origem_nfe_chave || "—"} quebra />
                </SimpleGrid>
              </Box>

              <Box bg="white" borderWidth="1px" borderRadius="lg" p={4}>
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="semibold">Itens devolvidos</Text>
                  <Text fontSize="xs" color="gray.500">
                    Total {formatBRL(registro.totais?.vNF || 0)}
                  </Text>
                </Flex>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th fontSize="10px">Código</Th>
                        <Th fontSize="10px">Descrição</Th>
                        <Th fontSize="10px">CFOP</Th>
                        <Th fontSize="10px" isNumeric>Qtd</Th>
                        <Th fontSize="10px" isNumeric>Valor</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(dados?.itens || []).length === 0 ? (
                        <Tr>
                          <Td colSpan={5} fontSize="xs" color="gray.500" textAlign="center" py={6}>
                            Os itens só são derivados depois que a NF-e de venda é lida.
                          </Td>
                        </Tr>
                      ) : (dados?.itens || []).map((item) => (
                        <Tr key={item.id}>
                          <Td fontSize="xs">{item.codigo}</Td>
                          <Td fontSize="xs"><Text noOfLines={1} maxW="240px">{item.descricao}</Text></Td>
                          <Td fontSize="xs">{item.cfop}</Td>
                          <Td fontSize="xs" isNumeric>{Number(item.quantidade)}</Td>
                          <Td fontSize="xs" isNumeric>{formatBRL(item.valor_total)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>

              {registro.status === "emitida" && (
                <Box bg="white" borderWidth="1px" borderRadius="lg" p={4}>
                  <Text fontSize="sm" fontWeight="semibold" mb={3}>NF-e de devolução</Text>
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} fontSize="xs" mb={3}>
                    <Info rotulo="Número" valor={registro.nfe_numero || "—"} />
                    <Info rotulo="Ambiente" valor={registro.ambiente === "producao" ? "Produção" : registro.ambiente === "homologacao" ? "Homologação" : "—"} />
                    <Info rotulo="Série" valor={registro.nfe_serie || "—"} />
                    <Info rotulo="Protocolo" valor={registro.nfe_protocolo || "—"} />
                    <Info rotulo="Emitida em" valor={formatDateTime(registro.emitida_em)} />
                    <Info rotulo="Chave" valor={registro.nfe_chave || "—"} quebra />
                  </SimpleGrid>
                  <Divider mb={3} />
                  <HStack>
                    <Button size="xs" variant="outline" leftIcon={<Download size={14} />}
                      isLoading={baixando === "xml"} onClick={() => baixar("xml")}>
                      XML
                    </Button>
                    <Button size="xs" variant="outline" leftIcon={<Download size={14} />}
                      isLoading={baixando === "danfe"} onClick={() => baixar("danfe")}>
                      DANFE
                    </Button>
                  </HStack>
                </Box>
              )}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function Info({ rotulo, valor, quebra = false }) {
  return (
    <Box>
      <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.02em">{rotulo}</Text>
      <Text fontSize="xs" wordBreak={quebra ? "break-all" : "normal"}>{valor}</Text>
    </Box>
  );
}
